import { changeSpace, selectedSpaceAtom } from "@/atoms/spaces"
import { NotificationSheet } from "@/modules/notifications/containers/NotificationSheet"
import { useTranslation } from "next-i18next"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { Icon } from "@/core/components/icon/Icon"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/core/components/Menu"
import { Divider } from "@/core/components/Divider"
import { Label } from "@/core/components/Label"
import { signOut } from "@/utils/api/authentication"
import useNavigationHistory from "@/core/hooks/useNavigationHistory"
import { useRouter } from "next/router"
import LanguageSwitcher from "../components/LanguageSwitcher"
import { cn } from "@/utils/helpers"
import { Illustration } from "@/core/components/Illustration"
import { Button } from "@/core/components/Button"

import { NavigationContext } from "./SideBar"
import { forwardRef, useContext } from "react"

export const Header = forwardRef<HTMLElement>(function Header(_, ref) {
  const { t } = useTranslation("navigation")
  const space = useRecoilValue(selectedSpaceAtom)
  const _changeSpace = useSetRecoilState(changeSpace)
  const { history, isSidePanelNavPending, goBack } = useNavigationHistory()
  const router = useRouter()
  const { open, setOpen } = useContext(NavigationContext)

  return (
    <header
      ref={ref}
      className={cn(
        "bg-surface top-0 z-30 flex h-20 w-full items-center justify-end pr-10 transition duration-200 2xl:justify-between",
      )}
    >
      <div className="h-header mr-auto flex w-fit items-center gap-4 px-4 py-4 2xl:hidden">
        <Button
          variant="text"
          size="icon"
          className="p-2"
          text={t("common:menu")}
          onClick={() => setOpen(true)}
        >
          <Icon icon={"menu"} size="1.5rem" />
        </Button>

        <div className="w-max">
          <Illustration
            name="flextribe-logo"
            alt="Logo symbol"
            height={24}
            width={145}
          />
        </div>
      </div>
      <div
        className="text-on-surface-variant text-md group hidden cursor-pointer items-center gap-2 pl-10 2xl:flex"
        onClick={goBack}
      >
        {history.length > 0 && !isSidePanelNavPending && (
          <>
            <Icon
              icon={"arrow_back"}
              size="1.5rem"
              className="transition-transform duration-300 ease-in-out group-hover:-translate-x-1"
            />
            <p className="opacity-100 transition-opacity duration-300 ease-in-out group-hover:opacity-100">
              {t("back_to_previous_page")}
            </p>
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        <NotificationSheet />
        <DropdownMenu>
          <DropdownMenuTrigger className="hover:bg-outline-variant/90 active:bg-outline-variant focus:bg-outline-variant text-md-medium flex w-fit cursor-pointer items-center justify-center gap-2 rounded-lg p-1 py-2 pl-3 leading-none outline-none">
            {space.name}
            <Icon icon={"arrow_drop_down"} size="1.5rem" />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-container-base">
            <div className="flex items-center gap-2 p-2 py-4">
              <Icon
                icon={"corporate_fare"}
                className="text-surface bg-on-surface flex h-10 w-10 items-center justify-center rounded-full p-2"
                size="1.5rem"
              />
              <div>
                <div className="text-md font-semibold">{space.name}</div>
                <div className="text-sm">
                  {t("vat", { value: space.reference })}
                </div>
              </div>
            </div>

            <Divider className="bg-on-surface-variant/12 my-0" />
            <div>
              <a href="https://flextribe.freshdesk.com/support/home">
                <Label
                  size="md"
                  className="hover:bg-on-surface/10 flex cursor-pointer items-center gap-2 px-3 py-4"
                >
                  <Icon icon="help" size="1.25rem" />
                  {t("help_center")}
                </Label>
              </a>
            </div>
            <div>
              <Label
                size="md"
                className="hover:bg-on-surface/10 flex cursor-pointer items-center gap-2 px-3 py-4"
                onClick={() => _changeSpace(true)}
              >
                <Icon icon="swap_horiz" size="1.25rem" />
                {t("change_space")}
              </Label>
            </div>
            <div>
              <Label
                size="md"
                className="hover:bg-on-surface/10 flex cursor-pointer items-center gap-2 px-3 py-4"
                onClick={() => router.push("/settings")}
              >
                <Icon icon="settings" size="1.25rem" />
                {t("settings")}
              </Label>
            </div>
            <Divider className="bg-on-surface-variant/12 my-0" />
            <div>
              <Label
                size="md"
                className="hover:bg-on-surface/10 flex cursor-pointer items-center gap-2 px-3 py-4"
                onClick={() => signOut()}
              >
                <Icon icon="logout" size="1.25rem" />
                {t("logout")}
              </Label>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <LanguageSwitcher />
      </div>
    </header>
  )
})
